import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Moon, Sun, Menu, X, Search, User, LogOut, LogIn, ShoppingCart,
  Wallet as WalletIcon, Sparkles, ChevronRight,
} from 'lucide-react';
import Logo from './Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import SearchModal from './SearchModal';
import TierBadge from './TierBadge';
import { api } from '../lib/api';

/**
 * Navbar (Iter-32 — mobile realignment).
 *
 * DESKTOP (≥ lg): traditional horizontal nav + right-cluster with wallet pill,
 *   tier badge, avatar-dropdown, theme toggle. Unchanged.
 *
 * MOBILE (< lg): the header row is intentionally kept short — Logo · Search ·
 *   Cart · Theme · Hamburger. Every user-related action (my_account,
 *   operative_pass, wallet, daily_spin, logout, wallet balance, tier badge)
 *   is consolidated into the hamburger drawer so the top row can never wrap
 *   or overflow on a 360-390px viewport. This kills the "floating avatar +
 *   detached dropdown" bug where the flex row overflowed.
 */

const Navbar = () => {
  const { theme, toggle } = useTheme();
  const { config } = useSiteConfig();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bal, setBal] = useState(null);
  const [tier, setTier] = useState(null);
  const nav = useNavigate();
  const location = useLocation();
  // Iter-37: user asked the navbar to be sticky ONLY on the home page.
  // On every other route the header scrolls away with the content.
  const isHome = location.pathname === '/';

  useEffect(() => {
    if (!user) { setBal(null); setTier(null); return; }
    const refresh = () => {
      api.walletGet().then(w => setBal(w?.balance ?? 0)).catch(() => setBal(null));
      api.mySubscription().then(s => setTier(s?.tier)).catch(() => setTier(null));
    };
    refresh();
    const onChanged = () => refresh();
    window.addEventListener('eh:tier-changed', onChanged);
    window.addEventListener('eh:wallet-changed', onChanged);
    return () => {
      window.removeEventListener('eh:tier-changed', onChanged);
      window.removeEventListener('eh:wallet-changed', onChanged);
    };
  }, [user]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault(); setSearchOpen(true);
      } else if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault(); setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock body scroll while the mobile drawer is open — otherwise the page
  // scrolls behind the fixed drawer overlay.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const doLogout = async () => { setOpen(false); setUserMenu(false); await logout(); nav('/'); };

  return (
    <header
      className={`${isHome ? 'sticky top-0' : 'relative'} z-40 backdrop-blur-md`}
      style={{ background: 'rgba(5,6,8,.78)', borderBottom: '1px solid var(--eh-border)' }}
      data-testid={`nav-header-${isHome ? 'sticky' : 'static'}`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4">
        {/* LOGO + BRAND */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0 flex-shrink" data-testid="nav-logo">
          <Logo size={36} className="sm:w-[42px] sm:h-[42px] shrink-0" />
          <div className="block min-w-0">
            <div className="eh-brand font-black tracking-widest text-sm sm:text-base leading-tight truncate">
              <span className="eh-title-error">ERROR</span><span className="eh-title-hacker">HACKER</span>
            </div>
            <div className="hidden sm:block text-[9px] sm:text-[10px] eh-mono opacity-60 leading-tight truncate">
              // underground tech intel
            </div>
          </div>
        </Link>

        {/* DESKTOP NAV LINKS */}
        <nav className="hidden lg:flex items-center gap-1">
          {config.nav.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) =>
                `eh-mono px-3 py-2 text-sm tracking-widest uppercase transition-colors ${
                  isActive ? 'text-[var(--eh-green)]' : 'text-[var(--eh-text)] hover:text-[var(--eh-green)]'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* RIGHT CLUSTER — mobile-slim: only essentials */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Search — desktop shows the pill, mobile hidden (accessible via drawer) */}
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="nav-search"
            className="hidden md:flex items-center gap-2 eh-mono text-xs px-3 py-2 rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors"
          >
            <Search size={14} /> <span className="opacity-70">search...</span>
            <kbd className="ml-2 text-[9px] opacity-60 px-1 py-0.5 border border-[var(--eh-border)] rounded">⌘K</kbd>
          </button>

          {/* Cart — always visible */}
          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors"
          >
            <ShoppingCart size={16} />
            {count > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] grid place-items-center text-[10px] eh-mono font-bold rounded-full px-1"
                style={{ background: 'var(--eh-green)', color: '#001a10' }}
              >
                {count}
              </span>
            )}
          </Link>

          {/* Desktop-only wallet pill + tier + avatar dropdown */}
          {user && (
            <>
              <Link
                to="/me/wallet"
                data-testid="nav-wallet-pill"
                className="hidden lg:inline-flex items-center gap-1.5 eh-mono text-xs px-2.5 py-2 rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors"
                title="My Wallet"
              >
                <WalletIcon size={12} className="text-[var(--eh-green)]" />
                <span className="eh-neon-soft font-bold">₹{Number(bal || 0).toLocaleString('en-IN')}</span>
              </Link>
              {tier && tier.rank > 0 && (
                <Link
                  to="/subscribe"
                  className="hidden lg:inline-flex"
                  title={`${tier.name} pass active`}
                  data-testid="nav-tier-badge"
                >
                  <TierBadge tier={tier} size="sm" />
                </Link>
              )}
              <div className="relative hidden lg:block">
                <button
                  data-testid="nav-user-btn"
                  onClick={() => setUserMenu(v => !v)}
                  className="flex items-center gap-2 px-2.5 py-2 justify-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div
                      className="w-6 h-6 rounded-full grid place-items-center text-[10px] eh-mono"
                      style={{ background: 'rgba(0,255,157,.15)', color: 'var(--eh-green)' }}
                    >
                      {(user.name || user.email)[0].toUpperCase()}
                    </div>
                  )}
                  <span className="eh-mono text-xs max-w-[110px] truncate">{user.name || user.email.split('@')[0]}</span>
                </button>
                {userMenu && (
                  <div
                    onMouseLeave={() => setUserMenu(false)}
                    className="absolute right-0 mt-2 w-44 eh-panel py-1 z-50"
                    style={{ background: '#0d1115' }}
                  >
                    <NavLink to="/me" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-3 py-2 text-xs eh-mono hover:bg-white/5"><User size={12} /> my_account</NavLink>
                    <NavLink to="/subscribe" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-3 py-2 text-xs eh-mono hover:bg-white/5"><Sparkles size={12} className="text-[#ffd34d]" /> operative_pass</NavLink>
                    <NavLink to="/me/wallet" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-3 py-2 text-xs eh-mono hover:bg-white/5"><WalletIcon size={12} /> wallet</NavLink>
                    <NavLink to="/me/spin" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-3 py-2 text-xs eh-mono hover:bg-white/5"><Sparkles size={12} className="text-[#ffd34d]" /> daily_spin</NavLink>
                    <button
                      data-testid="nav-logout"
                      onClick={doLogout}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs eh-mono hover:bg-white/5"
                    >
                      <LogOut size={12} /> logout
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {!user && (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden lg:flex items-center gap-1.5 eh-mono text-xs px-3 py-2 rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors"
            >
              <LogIn size={12} /> LOGIN
            </Link>
          )}

          {/* Theme toggle — always visible */}
          <button
            onClick={toggle}
            aria-label="toggle theme"
            className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {/* Hamburger — visible on mobile + tablet */}
          <button
            onClick={() => setOpen(o => !o)}
            className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 grid place-items-center rounded border border-[var(--eh-border)] hover:border-[var(--eh-green)] transition-colors"
            aria-label="menu"
            data-testid="nav-hamburger"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* MOBILE DRAWER — portal-rendered to document.body so the header's
          `backdrop-blur` (which creates a new containing block) doesn't trap
          `position:fixed` children inside a tiny 91px-tall header box. */}
      {open && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 top-[57px] z-[45] bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          {/* Drawer */}
          <div
            className="lg:hidden fixed inset-x-0 top-[57px] bottom-0 z-[46] overflow-y-auto"
            style={{ background: 'rgba(8,10,12,.98)', backdropFilter: 'blur(10px)' }}
            data-testid="nav-mobile-drawer"
          >
            {/* USER CARD (only when logged in) */}
            {user && (
              <div className="px-4 pt-4 pb-3 border-b border-[var(--eh-border)]">
                <div className="flex items-center gap-3">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-11 h-11 rounded-full shrink-0" />
                  ) : (
                    <div
                      className="w-11 h-11 rounded-full grid place-items-center font-bold text-base shrink-0"
                      style={{ background: 'rgba(0,255,157,.15)', color: 'var(--eh-green)', border: '1px solid rgba(0,255,157,.4)' }}
                    >
                      {(user.name || user.email)[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate" style={{ fontFamily: 'Inter,sans-serif' }} data-testid="drawer-user-name">
                      {user.name || user.email.split('@')[0]}
                    </div>
                    <div className="eh-mono text-[10px] opacity-60 truncate">{user.email}</div>
                  </div>
                </div>
                {/* Wallet + Tier pills */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Link
                    to="/me/wallet"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-1.5 eh-mono text-xs px-2.5 py-1.5 rounded border border-[rgba(0,255,157,.4)] bg-[rgba(0,255,157,.06)] hover:border-[var(--eh-green)] transition-colors"
                    data-testid="drawer-wallet-pill"
                  >
                    <WalletIcon size={12} className="text-[var(--eh-green)]" />
                    <span className="eh-neon-soft font-bold">₹{Number(bal || 0).toLocaleString('en-IN')}</span>
                  </Link>
                  {tier && tier.rank > 0 ? (
                    <Link to="/subscribe" onClick={() => setOpen(false)} data-testid="drawer-tier-badge">
                      <TierBadge tier={tier} size="sm" />
                    </Link>
                  ) : (
                    <Link
                      to="/subscribe"
                      onClick={() => setOpen(false)}
                      className="inline-flex items-center gap-1.5 eh-mono text-xs px-2.5 py-1.5 rounded border border-[rgba(255,211,77,.4)] bg-[rgba(255,211,77,.06)] hover:border-[#ffd34d] transition-colors"
                      style={{ color: '#ffd34d' }}
                      data-testid="drawer-upgrade-pill"
                    >
                      <Sparkles size={11} /> UPGRADE
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* NAV LINKS */}
            <div className="px-4 py-2">
              <div className="eh-mono text-[10px] tracking-widest opacity-50 pt-3 pb-2">// NAVIGATE</div>
              {config.nav.map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === '/'}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center justify-between eh-mono py-3 text-sm tracking-widest uppercase border-b border-[var(--eh-border)] ${
                      isActive ? 'text-[var(--eh-green)]' : ''
                    }`
                  }
                >
                  <span>{n.label}</span>
                  <ChevronRight size={14} className="opacity-30" />
                </NavLink>
              ))}
              <button
                onClick={() => { setOpen(false); setSearchOpen(true); }}
                className="w-full flex items-center justify-between eh-mono py-3 text-sm tracking-widest uppercase border-b border-[var(--eh-border)]"
                data-testid="drawer-search-btn"
              >
                <span className="flex items-center gap-2"><Search size={14} className="opacity-70" /> SEARCH</span>
                <ChevronRight size={14} className="opacity-30" />
              </button>
            </div>

            {/* ACCOUNT ACTIONS */}
            <div className="px-4 py-2">
              <div className="eh-mono text-[10px] tracking-widest opacity-50 pt-3 pb-2">
                {user ? '// ACCOUNT' : '// SIGN IN'}
              </div>
              {user ? (
                <>
                  <NavLink to="/me" onClick={() => setOpen(false)} data-testid="drawer-my-account" className="flex items-center gap-2.5 py-3 eh-mono text-sm border-b border-[var(--eh-border)] hover:text-[var(--eh-green)] transition-colors">
                    <User size={14} className="text-[var(--eh-green)]" /> my_account
                  </NavLink>
                  <NavLink to="/subscribe" onClick={() => setOpen(false)} data-testid="drawer-operative-pass" className="flex items-center gap-2.5 py-3 eh-mono text-sm border-b border-[var(--eh-border)] hover:text-[#ffd34d] transition-colors">
                    <Sparkles size={14} className="text-[#ffd34d]" /> operative_pass
                  </NavLink>
                  <NavLink to="/me/wallet" onClick={() => setOpen(false)} data-testid="drawer-wallet" className="flex items-center gap-2.5 py-3 eh-mono text-sm border-b border-[var(--eh-border)] hover:text-[var(--eh-green)] transition-colors">
                    <WalletIcon size={14} className="text-[var(--eh-green)]" /> wallet
                  </NavLink>
                  <NavLink to="/me/spin" onClick={() => setOpen(false)} data-testid="drawer-daily-spin" className="flex items-center gap-2.5 py-3 eh-mono text-sm border-b border-[var(--eh-border)] hover:text-[#ffd34d] transition-colors">
                    <Sparkles size={14} className="text-[#ffd34d]" /> daily_spin
                  </NavLink>
                  <button
                    onClick={doLogout}
                    data-testid="drawer-logout"
                    className="w-full text-left flex items-center gap-2.5 py-3 eh-mono text-sm border-b border-[var(--eh-border)] hover:text-[var(--eh-red)] transition-colors"
                  >
                    <LogOut size={14} /> logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="drawer-login"
                  className="w-full mt-1 flex items-center justify-center gap-2 py-3 rounded-md font-bold text-sm"
                  style={{ background: 'var(--eh-green)', color: '#000' }}
                >
                  <LogIn size={14} /> SIGN IN
                </Link>
              )}
            </div>

            <div className="px-4 py-6 eh-mono text-[10px] opacity-40 text-center">
              // errorhacker · v2.6
            </div>
          </div>
        </>,
        document.body
      )}

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};

export default Navbar;
